import { Link } from 'react-router-dom';
import { Home, Truck, Package, Clock, BarChart3, ChevronRight, Shirt, Plus, ShoppingBag, Scissors } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card, Button, LoadingState, ErrorState } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import { formatCurrency } from '@/lib/utils';
import type { DashboardStats } from '@/lib/types';

export function WardrobeHubPage() {
  const { data: stats, loading, error } = useApi<DashboardStats>('/dashboard/stats');

  const options = [
    { icon: Home, title: 'In Closet', description: 'Items currently in your wardrobe', count: stats?.inCloset, href: '/wardrobe/items?status=in-closet' },
    { icon: Truck, title: 'Out for Service', description: 'At cleaners, tailors, etc.', count: stats?.outForService, href: '/wardrobe/items?status=out-for-service' },
    { icon: Package, title: 'Pending Delivery', description: 'Items being shipped to you', count: stats?.pendingDelivery, href: '/wardrobe/items?status=pending-delivery' },
    { icon: ShoppingBag, title: 'Wishlist', description: 'Items you want to buy', count: stats?.wishlistCount, href: '/wardrobe/items?status=wishlist' },
    { icon: Clock, title: 'Unworn Report', description: 'Items you have not worn lately', count: stats?.unwornItems, href: '/wardrobe/items?unworn=1' },
    { icon: Scissors, title: 'Tailor Actions', description: 'Alterations to take in or bring back', count: stats?.pendingTailorActions, href: '/wardrobe/tailor' },
    { icon: BarChart3, title: 'Wardrobe Gap Report', description: 'Identify missing essentials', count: undefined, href: '/before-buy/gap-report' },
  ];

  return (
    <Page>
      <PageHeader section="Wardrobe" />
      <Main className="space-y-4">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}

        {stats && (
          <Card className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-secondary rounded-xl">
                <Shirt className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Items</p>
                <p className="text-2xl font-bold text-foreground">{stats.totalItems}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Total Value</p>
              <p className="text-xl font-semibold text-foreground">{formatCurrency(stats.totalValue)}</p>
            </div>
          </Card>
        )}

        <Link to="/wardrobe/add">
          <Button fullWidth>
            <Plus className="w-5 h-5" />
            Add New Item
          </Button>
        </Link>

        <div className="space-y-3">
          {options.map((option) => (
            <Link key={option.title} to={option.href}>
              <Card className="p-4 flex items-center gap-4 hover:border-primary/30 transition-colors group">
                <div className="p-3 bg-secondary rounded-xl group-hover:bg-accent/20 transition-colors">
                  <option.icon className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-foreground">{option.title}</h3>
                    {option.count !== undefined && (
                      <span className="px-2 py-0.5 bg-secondary rounded-full text-xs font-medium text-muted-foreground">
                        {option.count}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{option.description}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </Card>
            </Link>
          ))}
        </div>
      </Main>
    </Page>
  );
}
